'use client';

import { Fragment } from 'react';
import Link from 'next/link';
import styles from './ArticlePreview.module.scss';
import { Locale } from '@/i18n-config';
import { gaClick, gaCta } from '@/src/helpers/gtag';
import { useAOS } from '@/src/lib/hooks/useAOS';
import { Article } from '@/src/types/Header';

interface ArticlePreviewProps {
  eyebrow?: string;
  title?: string;
  highlightedTitle?: string;
  description?: string;
  ctaText?: string;
  ctaHref?: string;
  readMoreText?: string;
  articles?: Article[];
  lang?: Locale;
  tilt?: 'left' | 'right';
  id?: string;
}

const renderTitle = (text?: string) => {
  if (!text) {
    return null;
  }
  const parts = text.split('*');
  return parts.map((part, i) =>
    i % 2 === 1 ? <em key={i}>{part}</em> : <Fragment key={i}>{part}</Fragment>
  );
};

function articleImageUrl(src?: string) {
  if (!src) {
    return undefined;
  }
  if (src.startsWith('http')) {
    return `/api/image-proxy?url=${encodeURIComponent(src)}`;
  }
  return src;
}

export function ArticlePreview({
  eyebrow = 'INSIGHTS',
  title = 'Nos derniers articles',
  highlightedTitle = '*pour aller plus loin*',
  description = 'Retours d’expérience, méthodes et conseils pratiques autour du delivery, de la tech et du pilotage de projets.',
  ctaText = 'Voir tous les articles',
  ctaHref = '/articles',
  readMoreText = 'Lire l’article',
  articles = [],
  lang = 'fr',
  tilt = 'right',
  id,
}: ArticlePreviewProps) {
  useAOS();

  const tiltClass = tilt === 'left' ? styles.tiltLeft : styles.tiltRight;
  const visibleArticles = articles.slice(0, 3);
  const resolvedCtaHref =
    ctaHref.startsWith('/') && !ctaHref.startsWith(`/${lang}/`) ? `/${lang}${ctaHref}` : ctaHref;

  return (
    <section id={id} className={`${styles.root} ${tiltClass}`}>
      <div className={styles.bg} aria-hidden="true" />
      <div className={styles.inner}>
        <div className={styles.header} data-aos="fade-up" data-aos-duration="700">
          {eyebrow && <p className={styles.eyebrow}>{eyebrow}</p>}
          <h2 className={styles.title}>
            {renderTitle(title)}{' '}
            <span className={styles.highlight}>{renderTitle(highlightedTitle)}</span>
          </h2>
          {description && <p className={styles.description}>{description}</p>}
        </div>

        {visibleArticles.length > 0 ? (
          <div
            className={styles.grid}
            data-aos="fade-up"
            data-aos-duration="800"
            data-aos-delay="100"
          >
            {visibleArticles.map((article) => {
              const articleSlug = article.slug || article.title?.replace(/\s+/g, '-');
              const href = `/articles/${encodeURIComponent(articleSlug)}`;
              const imageSrc = articleImageUrl(article.mainImage);
              return (
                <article key={article.id || article._id} className={styles.card}>
                  <Link
                    href={href}
                    className={styles.imageWrap}
                    aria-label={article.title}
                    onClick={() =>
                      gaClick('article_preview_card', { article_title: article.title, href })
                    }
                  >
                    {imageSrc ? (
                      <img
                        src={imageSrc}
                        alt={article.title}
                        loading="lazy"
                        className={styles.image}
                      />
                    ) : (
                      <div className={styles.imagePlaceholder} />
                    )}
                  </Link>
                  <div className={styles.content}>
                    <h3 className={styles.cardTitle}>
                      <Link
                        href={href}
                        onClick={() =>
                          gaClick('article_preview_card', { article_title: article.title, href })
                        }
                      >
                        {article.title}
                      </Link>
                    </h3>
                    {article.description && (
                      <p className={styles.cardDesc}>{article.description}</p>
                    )}
                    <Link
                      href={href}
                      className={styles.cardCta}
                      onClick={() => gaCta(readMoreText, 'article_preview_cta', href)}
                    >
                      {readMoreText}
                    </Link>
                  </div>
                </article>
              );
            })}
          </div>
        ) : (
          <p className={styles.description} style={{ textAlign: 'center' }}>
            Aucun article disponible pour le moment.
          </p>
        )}

        {ctaText && ctaHref && (
          <div
            className={styles.ctaRow}
            data-aos="fade-up"
            data-aos-duration="700"
            data-aos-delay="200"
          >
            <Link
              href={resolvedCtaHref}
              className={styles.cta}
              onClick={() => gaCta(ctaText, 'article_preview_cta', resolvedCtaHref)}
            >
              {ctaText}
            </Link>
          </div>
        )}
      </div>
    </section>
  );
}
